<div class="d-flex flex-column gap-3" data-testid="invoice-table-panel">
  <div class="d-flex flex-column gap-1">
    <h3 class="av-section-title m-0">{{ 'backoffice.billing.invoices.title' | translate }}</h3>
    <p class="av-meta m-0">{{ 'backoffice.billing.invoices.advance_note' | translate }}</p>
  </div>

  @switch (facade.invoicesState()) {
    @case ('error') {
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.billing.invoices.error_title' | translate"
        [message]="'backoffice.billing.invoices.error_message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      <div class="filter-row d-flex flex-wrap align-items-end gap-2">
        <label class="d-flex flex-column gap-1" for="invoice-status">
          <span class="av-meta">{{ 'backoffice.billing.invoices.filters.status' | translate }}</span>
          <select
            id="invoice-status"
            class="av-field"
            [value]="facade.invoiceFilters().stripe_status"
            (change)="changeFilter('stripe_status', $any($event.target).value)"
            data-testid="invoice-status-filter"
          >
            <option value="">{{ 'backoffice.billing.invoices.filters.any_status' | translate }}</option>
            @for (option of statusOptions; track option) {
              <option [value]="option">{{ statusLabelKeys[option] | translate }}</option>
            }
          </select>
        </label>

        <label class="d-flex flex-column gap-1" for="invoice-attention">
          <span class="av-meta">{{ 'backoffice.billing.invoices.filters.attention' | translate }}</span>
          <select
            id="invoice-attention"
            class="av-field"
            [value]="facade.invoiceFilters().attention"
            (change)="changeFilter('attention', $any($event.target).value)"
            data-testid="invoice-attention-filter"
          >
            <option value="">{{ 'backoffice.billing.invoices.filters.everything' | translate }}</option>
            <option value="1">{{ 'backoffice.billing.invoices.filters.needs_attention' | translate }}</option>
          </select>
        </label>

        <label class="d-flex flex-column gap-1" for="invoice-from">
          <span class="av-meta">{{ 'backoffice.billing.invoices.filters.from' | translate }}</span>
          <input
            id="invoice-from"
            type="date"
            class="av-field"
            [value]="facade.invoiceFilters().from"
            (change)="changeFilter('from', $any($event.target).value)"
          />
        </label>

        <label class="d-flex flex-column gap-1" for="invoice-to">
          <span class="av-meta">{{ 'backoffice.billing.invoices.filters.to' | translate }}</span>
          <input
            id="invoice-to"
            type="date"
            class="av-field"
            [value]="facade.invoiceFilters().to"
            (change)="changeFilter('to', $any($event.target).value)"
          />
        </label>

        @if (facade.hasInvoiceFilters()) {
          <button
            type="button"
            class="av-btn"
            (click)="clearFilters()"
            data-testid="invoice-clear-filters"
          >
            {{ 'backoffice.billing.invoices.filters.clear' | translate }}
          </button>
        }
      </div>

      @if (isEmpty()) {
        <av-empty-state
          icon="description"
          [title]="
            (facade.hasInvoiceFilters()
              ? 'backoffice.billing.invoices.filtered_empty_title'
              : 'backoffice.billing.invoices.empty_title'
            ) | translate
          "
          [message]="
            (facade.hasInvoiceFilters()
              ? 'backoffice.billing.invoices.filtered_empty_message'
              : 'backoffice.billing.invoices.empty_message'
            ) | translate
          "
        ></av-empty-state>
      } @else {
        <p class="av-meta m-0" data-testid="invoice-snapshot-note">{{ 'backoffice.billing.invoices.snapshot_note' | translate }}</p>

        <div class="table-scroll">
          <table class="av-table w-100" data-testid="invoice-table">
            <thead>
              <tr>
                <th scope="col">{{ 'backoffice.billing.invoices.table.invoice' | translate }}</th>
                <th scope="col">{{ 'backoffice.billing.invoices.table.status' | translate }}</th>
                <th scope="col">{{ 'backoffice.billing.invoices.table.plan' | translate }}</th>
                <th scope="col">{{ 'backoffice.billing.invoices.table.period' | translate }}</th>
                <th scope="col" class="text-end">{{ 'backoffice.billing.invoices.table.seats' | translate }}</th>
                <th scope="col" class="text-end">{{ 'backoffice.billing.invoices.table.devices' | translate }}</th>
                <th scope="col" class="text-end">{{ 'backoffice.billing.invoices.table.total' | translate }}</th>
                <th scope="col"></th>
              </tr>
            </thead>

            <tbody>
              @for (invoice of facade.invoices(); track invoice.id) {
                <tr>
                  <td>
                    <a
                      class="cell-strong text-decoration-none"
                      [routerLink]="[
                        '/organizations',
                        facade.organizationId(),
                        'billing',
                        'invoices',
                        invoice.id,
                      ]"
                      data-testid="invoice-link"
                    >
                      {{ invoice.stripe_invoice_id || unresolved }}
                    </a>
                  </td>

                  <td>
                    <av-status-chip
                      [label]="invoice.statusLabelKey | translate"
                      [tone]="invoice.statusTone"
                    ></av-status-chip>
                  </td>

                  <td class="cell-muted">{{ invoice.plan_name || unresolved }}</td>

                  <td class="cell-muted">
                    {{ invoice.period_start ? (invoice.period_start | date: dateFormat) : unresolved }}
                    —
                    {{ invoice.period_end ? (invoice.period_end | date: dateFormat) : unresolved }}
                  </td>

                  <td class="text-end" data-testid="invoice-seat-quantity">
                    {{ invoice.seat_quantity ?? unresolved }}
                  </td>

                  <td class="text-end" data-testid="invoice-device-quantity">
                    {{ invoice.device_quantity ?? unresolved }}
                  </td>

                  <td class="text-end">
                    {{ invoice.total === null ? unresolved : (invoice.total | number: '1.2-2') }}
                    {{ invoice.currency || '' }}
                  </td>

                  <td>
                    <button
                      type="button"
                      class="av-btn"
                      (click)="openDocument(invoice.id)"
                      data-testid="invoice-open-document"
                    >
                      <mat-icon aria-hidden="true">open_in_new</mat-icon>
                      <span>{{ 'backoffice.billing.invoice_actions.pdf' | translate }}</span>
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <av-paginator
          [page]="facade.invoicePage()"
          [pageSize]="facade.pageSize()"
          [total]="facade.invoiceTotal()"
          (pageChange)="goToPage($event)"
          (pageSizeChange)="changePageSize($event)"
        ></av-paginator>
      }
    }
    @default {
      <av-empty-state
        icon="hourglass_empty"
        [title]="'backoffice.billing.invoices.loading_title' | translate"
        [message]="'backoffice.billing.invoices.loading_message' | translate"
      ></av-empty-state>
    }
  }
</div>
